import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { t } from "../locale";
import { initializeAnalytics } from "../portal/analytics";
import { deviceRequest, ensureDeviceAccount } from "./device-session";
export type Viewer = {
  userId: string;
  accountType: "device";
  deviceId: string;
  devicePlatform: string;
  nickname: string;
  avatar: string;
  enabled: boolean;
  revision: number;
  createdAt: string;
  lastSeenAt?: string;
};
export type LibraryItem = {
  seriesId: string;
  title: string;
  saved: boolean;
  progress: { episode: number; seconds: number; updatedAt: number } | null;
};
type Change = {
  seriesId: string;
  title: string;
  saved?: boolean;
  progress?: { episode: number; seconds: number } | null;
};
type Library = { revision: number; items: LibraryItem[] };
type ViewerContextType = {
  user: Viewer | null;
  ready: boolean;
  error: string;
  guestId: string;
  items: LibraryItem[];
  refresh: () => Promise<void>;
  updateProfile: (nickname: string, avatar: string) => Promise<void>;
  changeItem: (change: Change) => Promise<void>;
};
const Context = createContext<ViewerContextType | null>(null);
export const useViewer = () => {
  const value = useContext(Context);
  if (!value) throw new Error("ViewerProvider required");
  return value;
};
export function ViewerProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Viewer | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState<LibraryItem[]>([]);
  const revision = useRef(0);
  const owner = useRef("");
  const mounted = useRef(false);
  const refreshTask = useRef<Promise<void> | null>(null);
  const queue = useRef<Promise<void>>(Promise.resolve());
  function applyLibrary(data: Library) {
    if (data.revision >= revision.current) {
      revision.current = data.revision;
      setItems(data.items);
    }
  }
  function fail(error: unknown) {
    setError(
      error instanceof Error
        ? error.message
        : t("Could not connect to your account."),
    );
    setUser(null);
    setItems([]);
    revision.current = 0;
  }
  function refresh(): Promise<void> {
    if (refreshTask.current) return refreshTask.current;
    refreshTask.current = (async () => {
      try {
        await ensureDeviceAccount();
        const profile = await deviceRequest<Viewer>("account/me");
        // Never attach old client data to a new account after storage loss.
        if (owner.current !== profile.userId) {
          revision.current = 0;
          setItems([]);
        }
        owner.current = profile.userId;
        const library = await deviceRequest<Library>("library");
        if (!mounted.current) return;
        setUser(profile);
        applyLibrary(library);
        setError("");
      } catch (error) {
        if (mounted.current) fail(error);
      } finally {
        if (mounted.current) setReady(true);
        refreshTask.current = null;
      }
    })();
    return refreshTask.current;
  }
  useEffect(() => {
    mounted.current = true;
    void refresh().then(() => {
      void initializeAnalytics();
    });
    const focus = () => {
      void refresh();
    };
    addEventListener("focus", focus);
    addEventListener("online", focus);
    return () => {
      mounted.current = false;
      removeEventListener("focus", focus);
      removeEventListener("online", focus);
    };
  }, []);
  async function updateProfile(nickname: string, avatar: string) {
    if (!user) throw new Error(t("Could not connect to your account."));
    const profile = await deviceRequest<Viewer>("account/me", "PUT", {
      nickname,
      avatar,
      expectedRevision: user.revision,
    });
    if (mounted.current) {
      setUser(profile);
      setError("");
    }
  }
  function changeItem(change: Change): Promise<void> {
    if (!user)
      return Promise.reject(new Error(t("Could not connect to your account.")));
    const expectedOwner = user.userId;
    const action = queue.current
      .catch(() => {})
      .then(async () => {
        if (expectedOwner !== owner.current)
          throw new Error(t("Account environment mismatch."));
        try {
          const next = await deviceRequest<Library>("library", "PUT", change);
          if (mounted.current && expectedOwner === owner.current) {
            applyLibrary(next);
            setError("");
          }
        } catch (error) {
          if (mounted.current)
            setError(
              t(
                "Your latest change could not be synced. Please retry when connected.",
              ),
            );
          throw error;
        }
      });
    queue.current = action;
    return action;
  }
  return (
    <Context.Provider
      value={{
        user,
        ready,
        error,
        guestId: user?.userId || "",
        items,
        refresh,
        updateProfile,
        changeItem,
      }}
    >
      {user ? (
        children
      ) : (
        <main className="device-account-gate" aria-live="polite">
          <a href="/" className="wordmark">
            yes<span>tv</span>
            <i>.</i>
          </a>
          <h1>
            {t(
              ready
                ? "Account connection unavailable"
                : "Preparing your account…",
            )}
          </h1>
          <p>
            {error ||
              t(
                "Your account is created automatically. No registration needed.",
              )}
          </p>
          {ready && (
            <button
              className="account-primary"
              onClick={() => {
                setReady(false);
                void refresh();
              }}
            >
              {t("Retry connection")}
            </button>
          )}
        </main>
      )}
    </Context.Provider>
  );
}
